import {
  KeyboardArrowDownOutlined,
  KeyboardArrowRightOutlined,
} from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { filter, isEqual } from 'lodash';
import {
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { FilterList } from 'src/components/filterList';
import { Footer } from 'src/components/footer/Footer';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { queryClient } from 'src/components/queryProvider';
import { GenericStepper } from 'src/components/stepper';
import {
  SORT_DIRECTION,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { CustomTable } from 'src/components/table/CustomTable';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { ControlType } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import {
  BULK_REPLACE_EXPORT_HEADERS,
  COLLECTIONS,
  INoScreens,
  IOrientation,
  IOrientationLabel,
  USER_ACTIONS,
} from 'src/constants';
import { PATHS } from 'src/constants/paths';
import { useModal, useParamQuery } from 'src/hooks';
import { decodeToken, getToken } from 'src/hooks';
import { useSaveBulkReplaceComponent } from 'src/hooks/api/domainSpecific/bulkReplaceComponent/useSaveBulkReplaceComponent';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useNavigateBlocker } from 'src/hooks/navigation/useNavigateBlocker';
import { useFilterAndSorting } from 'src/hooks/useFilterAndSorting';
import { ComponentTypes, PublicationTypes } from 'src/interfaces';
import { IFilter } from 'src/interfaces/Filter';
import { escapeCsvValue, lang, queryKeys } from 'src/utils';

import {
  getScheduleItemSelectionKey,
  hasDateConflict,
  mapScheduleListToTableRows,
} from './BulkReplaceComponentPage.helper';
import { useBulkReplaceComponentPage } from './BulkReplaceComponentPage.hook';
import {
  IBulkReplaceExportData,
  IBulkReplaceScheduleItem,
  IBulkReplaceScheduleRow,
  IBulkReplaceTableRow,
} from './BulkReplaceComponentPage.interface';
import { ComponentBulkReplaceContainer } from './BulkReplaceComponentPage.styled';
import { useComponentData } from './hooks/useComponentData';
import { BulkReplaceComponentReviewStep } from './modules/BulkReplaceComponentReviewStep';
import { ComponentReplaceSection } from './modules/ComponentReplaceSection';
import { Header } from './modules/Header';

export const BulkReplaceComponentPage = () => {
  const { originalComponent, replacementComponent } = useComponentData();
  const publishModal = useModal();
  const saveModal = useModal();
  const saveBulkReplaceComponent = useSaveBulkReplaceComponent();
  const { getParamsValue } = useParamQuery();
  const navigate = useNavigate();
  const { userRole } = useContext(AuthDataProvider);
  const userAuthInfo = decodeToken(getToken() || '');
  const emailKey = Object.keys(userAuthInfo).find((key) =>
    key.endsWith('/email'),
  );

  const {
    activeFilters,
    onChangeParams,
    scheduleList,
    isLoading,
    rowsPerPage,
    currentPage,
    selectedSortOption,
    isAllSelected,
    selectedLocationGroups,
    selectedScheduleItems,
    selectedLocationGroupsData,
    expandedLocationGroup,
    handleCancel,
    handleSelectAll,
    handleClearSelection,
    handleRowsPerPageChange,
    handlePageChange,
    handleSortChange,
    handleSelectLocationGroup,
    handleSelectScheduleItem,
    matchedNames,
    setMatchedNames,
    isMatchLoading,
  } = useBulkReplaceComponentPage();

  const [showLeaveModal, setShowLeaveModal] = useState(true);
  const showLeaveModalRef = useRef(showLeaveModal);

  const updateShowLeaveModal = useCallback((value: boolean) => {
    showLeaveModalRef.current = value;
    setShowLeaveModal(value);
  }, []);

  useNavigateBlocker(() => showLeaveModalRef.current);

  const [activeStep, setActiveStep] = useState(0);
  const [expandedRowIndex, setExpandedRowIndex] = useState<number>(-1);

  const [csvData, setCsvData] = useState<IBulkReplaceExportData[]>([]);
  const csvLinkRef = useRef<any>(null);

  const deferredSelectedScheduleItems = useDeferredValue(selectedScheduleItems);
  const deferredSelectedLocationGroups = useDeferredValue(
    selectedLocationGroups,
  );

  const generateExportData = useCallback(() => {
    if (!selectedLocationGroupsData || selectedLocationGroupsData.length === 0)
      return [];

    const data: IBulkReplaceExportData[] = [];

    selectedLocationGroupsData.forEach((locationGroup) => {
      const scheduleItems = locationGroup.scheduleItems || [];
      const itemsToExport = filter(
        scheduleItems,
        (item: IBulkReplaceScheduleItem) =>
          selectedScheduleItems.has(
            getScheduleItemSelectionKey(
              locationGroup.location_group_id,
              item.id,
            ),
          ),
      );

      itemsToExport.forEach((scheduleItem: IBulkReplaceScheduleItem) => {
        const start = scheduleItem.start_date
          ? dayjs(scheduleItem.start_date).format('MM-DD-YYYY')
          : '';
        const end = scheduleItem.end_date
          ? dayjs(scheduleItem.end_date).format('MM-DD-YYYY')
          : '';
        const lastModifiedDate = scheduleItem.modified_at
          ? dayjs(scheduleItem.modified_at).format('MM-DD-YYYY')
          : '';
        data.push({
          locationGroupName: escapeCsvValue(
            locationGroup.location_group_name || '',
          ),
          scheduleName: escapeCsvValue(scheduleItem.scheduleName),
          scheduleId: scheduleItem.scheduleId,
          daypart: escapeCsvValue(scheduleItem.daypart_name || ''),
          startDate: escapeCsvValue(start),
          endDate: escapeCsvValue(end),
          originalComponent: escapeCsvValue(originalComponent?.name || ''),
          fromComponentId: originalComponent?.id,
          replacementComponent: escapeCsvValue(
            replacementComponent?.name || '',
          ),
          toComponentId: replacementComponent?.id,
          userRole: escapeCsvValue(userRole || ''),
          userId: emailKey ? userAuthInfo[emailKey] : '',
          lastModified: escapeCsvValue(lastModifiedDate),
        } as unknown as IBulkReplaceExportData);
      });
    });

    return data;
  }, [
    selectedLocationGroupsData,
    selectedScheduleItems,
    originalComponent,
    replacementComponent,
    userRole,
    emailKey,
    userAuthInfo,
  ]);

  const handleExportClick = useCallback(() => {
    const data = generateExportData();
    if (data.length === 0) return;

    setCsvData(data);
    setTimeout(() => {
      csvLinkRef.current?.link?.click();
    }, 0);
  }, [generateExportData]);

  const handleRowExpand = useCallback((index: number) => {
    setExpandedRowIndex(index);
  }, []);
  const sortItems = [
    {
      label: lang.dateCreated,
      isSelected: selectedSortOption?.option === SortOptions.DATE_CREATED,
      onClick: () => {
        const newOrder =
          selectedSortOption?.option === SortOptions.DATE_CREATED &&
          selectedSortOption?.order === SORT_DIRECTION.DESC
            ? SORT_DIRECTION.ASC
            : SORT_DIRECTION.DESC;
        handleSortChange({
          option: SortOptions.DATE_CREATED,
          order: newOrder,
        });
      },
    },
    {
      label: lang.aToZ,
      isSelected: selectedSortOption?.option === SortOptions.ALPHABETICALLY,
      onClick: () => {
        const newOrder =
          selectedSortOption?.option === SortOptions.ALPHABETICALLY &&
          selectedSortOption?.order === SORT_DIRECTION.ASC
            ? SORT_DIRECTION.DESC
            : SORT_DIRECTION.ASC;
        handleSortChange({
          option: SortOptions.ALPHABETICALLY,
          order: newOrder,
        });
      },
    },
    {
      label: lang.lastModified,
      isSelected: selectedSortOption?.option === SortOptions.LAST_MODIFIED,
      onClick: () => {
        const newOrder =
          selectedSortOption?.option === SortOptions.LAST_MODIFIED &&
          selectedSortOption?.order === SORT_DIRECTION.DESC
            ? SORT_DIRECTION.ASC
            : SORT_DIRECTION.DESC;
        handleSortChange({
          option: SortOptions.LAST_MODIFIED,
          order: newOrder,
        });
      },
    },
  ];

  const tableRows = useMemo(
    () =>
      mapScheduleListToTableRows(
        scheduleList,
        deferredSelectedLocationGroups,
        deferredSelectedScheduleItems,
        handleSelectScheduleItem,
        false,
        replacementComponent,
      ),
    [
      handleSelectScheduleItem,
      scheduleList,
      deferredSelectedLocationGroups,
      deferredSelectedScheduleItems,
      replacementComponent,
    ],
  );

  const effectiveSelectedLocationGroupsCount = useMemo(() => {
    return selectedLocationGroupsData.length;
  }, [selectedLocationGroupsData]);

  const requestScheduleItems = useMemo(() => {
    if (
      !selectedLocationGroupsData ||
      selectedLocationGroupsData.length === 0
    ) {
      return [];
    }

    return selectedLocationGroupsData.flatMap((locationGroup) =>
      (locationGroup.scheduleItems || [])
        .filter(
          (item) =>
            selectedScheduleItems.has(
              getScheduleItemSelectionKey(
                locationGroup.location_group_id,
                item.id,
              ),
            ) && !hasDateConflict(item, replacementComponent),
        )
        .map((item) => ({
          id: item.id,
          schedule_id: item.scheduleId,
          schedule_name: item.scheduleName,
          daypart_name: item.daypart_name,
        })),
    );
  }, [selectedLocationGroupsData, selectedScheduleItems, replacementComponent]);

  useEffect(() => {
    const nameListPrams = (getParamsValue('name.list') as string[]) || [];
    const allNames = tableRows.flatMap((row: IBulkReplaceTableRow) => [
      row.location_group,
      ...(row.rows
        ? row.rows.map((item: IBulkReplaceScheduleRow) => item.schedule_name)
        : []),
    ]);
    const updated = nameListPrams.map((name) => ({
      name,
      matched: allNames.some(
        (n: string | undefined) => n && n.toLowerCase() === name.toLowerCase(),
      ),
    }));
    if (!isEqual(matchedNames, updated)) {
      setMatchedNames(updated);
    }
  }, [
    replacementComponent,
    getParamsValue,
    matchedNames,
    tableRows,
    setMatchedNames,
  ]);

  const filters: IFilter[] = [
    {
      label: lang.numberOfScreens,
      options: Object.values(INoScreens).map((value: INoScreens) => ({
        value: value,
        label: value,
      })),
    },
    {
      label: lang.screenOrientation,
      options: Object.values(IOrientation).map((value: IOrientation) => ({
        value: value,
        label: IOrientationLabel[value],
      })),
    },
  ];

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );
  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
  } = useFilterAndSorting(filters, [], isFuzzySearch);

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
  };

  const onReset = () => {
    onChangeParams('name.regexp', []);
    onChangeParams('name.list', []);
    onChangeParams('num_screens.list', []);
    onChangeParams('orientation.list', []);
    setIsFuzzySearch(true);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const checkComponentTypeIsDifferent = useMemo(
    () =>
      originalComponent?.component_type?.name ===
        ComponentTypes.PROMOTIONAL_SPACE &&
      originalComponent?.component_type_id !==
        replacementComponent?.component_type_id,
    [originalComponent, replacementComponent],
  );

  const handleSave = async () => {
    if (!replacementComponent || !originalComponent) return;

    const requestData = requestScheduleItems.map((item) => ({
      id: item.id,
      component_id: replacementComponent.id,
      narrative: narrativeGenerators[COLLECTIONS.SCHEDULE_ITEMS][
        USER_ACTIONS.UPDATE
      ](originalComponent?.name, replacementComponent?.name, item.id),
      deleteBackup: checkComponentTypeIsDifferent,
    }));

    saveBulkReplaceComponent.mutate(requestData, {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.scheduleList.invalidateAll());
        saveModal.onToggleModal();
        publishModal.onToggleModal();
      },
    });
  };

  const handlePublishModalClose = () => {
    updateShowLeaveModal(false);
    publishModal.onCloseModal();
    navigate(PATHS.COMPONENTS);
  };

  const steps = [
    {
      name: 'Bulk Replace',
      component: (
        <Grid container spacing={4} sx={{ flex: 1, display: 'flex' }}>
          <Grid item xs={12} md={3}>
            <FilterList
              enableSearchArea
              filters={filters}
              onChangeKeyword={onChangeKeyword}
              onRemoveKeyword={onRemoveKeyword}
              onChangeFilter={onChangeParams}
              showReset
              onReset={onReset}
              hasExactSearch
              onSearchTypeChange={onSearchTypeChange}
              isFuzzySearch={isFuzzySearch}
              isMatchStatusLoading={isMatchLoading}
              matchedNames={matchedNames ?? []}
            />
          </Grid>

          <Grid
            container
            item
            xs={12}
            md={9}
            flexDirection="column"
            sx={{
              flex: 1,
              display: 'flex',
              paddingBottom: '50px',
            }}>
            <ComponentReplaceSection
              onChangeComponent={() => updateShowLeaveModal(false)}
            />

            <Grid
              item
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}>
              <CustomTableWithPagination
                key={`table-${expandedLocationGroup}`}
                loading={isLoading}
                columns={[
                  {
                    id: 'actions',
                    label: lang.locationGroup,
                    width: '27%',
                  },
                  {
                    id: 'channel_type',
                    label: lang.bulkReplaceComponentPage.channelType,
                    width: '21.5%',
                  },
                  {
                    id: 'number_of_schedules',
                    label: lang.bulkReplaceComponentPage.numberOfSchedules,
                    width: '30%',
                    align: 'left',
                  },
                ]}
                rows={tableRows}
                totalCount={scheduleList?.totalCount || 0}
                totalPages={scheduleList?.totalPages || 1}
                page={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                showTableControls
                onRowsPerPageChange={handleRowsPerPageChange}
                selectedSortOption={selectedSortOption}
                sortItems={sortItems}
                onSortChange={handleSortChange}
                visibleControls={[
                  ControlType.SORT,
                  ControlType.ITEMS_PER_PAGE,
                  ...(effectiveSelectedLocationGroupsCount > 0
                    ? [ControlType.CLEAR_SECTION]
                    : []),
                ]}
                selectedCount={effectiveSelectedLocationGroupsCount}
                onClearSelection={handleClearSelection}
                expandedRowIndex={expandedRowIndex}
                onRowExpand={handleRowExpand}
                renderActions={(row) => (
                  <Grid
                    container
                    item
                    wrap="nowrap"
                    alignItems="center"
                    sx={{ pl: 1 }}>
                    <Grid item>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          const rowIndex = tableRows.findIndex(
                            (r: IBulkReplaceTableRow) => r.id === row.id,
                          );
                          handleRowExpand(
                            expandedRowIndex === rowIndex ? -1 : rowIndex,
                          );
                        }}
                        sx={{ mr: 1 }}>
                        {expandedRowIndex ===
                        tableRows.findIndex(
                          (r: IBulkReplaceTableRow) => r.id === row.id,
                        ) ? (
                          <KeyboardArrowDownOutlined />
                        ) : (
                          <KeyboardArrowRightOutlined />
                        )}
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={row.isSelected}
                            indeterminate={row.isIndeterminate}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectLocationGroup(row.id);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        }
                        label={''}
                        sx={{ mr: 2 }}
                      />
                    </Grid>
                    <Grid item>
                      <CustomTooltip
                        title={row.location_group || ''}
                        overflowWidth={200}
                        placement="right">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.location_group}
                        </Typography>
                      </CustomTooltip>
                    </Grid>
                  </Grid>
                )}
                renderHeadActions={(col) => (
                  <Grid
                    container
                    item
                    wrap="nowrap"
                    alignItems="center"
                    sx={{ pl: 1 }}>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={
                              (selectedLocationGroups.size > 0 ||
                                selectedScheduleItems.size > 0) &&
                              !isAllSelected
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectAll();
                            }}
                          />
                        }
                        label={
                          <Grid container alignItems="center" spacing={0.5}>
                            <Grid item>
                              <Typography
                                sx={{
                                  fontWeight: 'bold',
                                  fontSize: '14px',
                                }}>
                                {col.label}
                              </Typography>
                            </Grid>
                          </Grid>
                        }
                        sx={{ mr: 2 }}
                      />
                    </Grid>
                  </Grid>
                )}
                renderCollapseContent={(row: IBulkReplaceTableRow) => {
                  if (!row) return null;
                  const rowIndex = tableRows.findIndex(
                    (r: IBulkReplaceTableRow) => r.id === row.id,
                  );

                  return expandedRowIndex === rowIndex ? (
                    <CustomTable
                      overrideHeight="auto"
                      columns={row.columns}
                      rows={row.rows}
                      loading={false}
                      stickyHeader={false}
                      tableCellHeadProps={{
                        sx: {
                          backgroundColor: 'rgba(2, 90, 197, 0.04)',
                        },
                      }}
                      tableBodyRowProps={{
                        sx: {
                          backgroundColor: 'rgba(2, 90, 197, 0.04)',
                          ':hover': {
                            backgroundColor: 'rgba(2, 90, 197, 0.04)',
                          },
                        },
                      }}
                    />
                  ) : null;
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      ),
      footer: (
        <Footer
          cancelLabel={lang.cancel}
          saveLabel={lang.next}
          onCancel={handleCancel}
          onSave={handleNext}
          disableSave={selectedScheduleItems.size === 0}
          showFooterActions
        />
      ),
    },
    {
      name: 'Review',
      component: (
        <BulkReplaceComponentReviewStep
          isLoading={isLoading}
          selectedLocationGroups={selectedLocationGroups}
          selectedLocationGroupsData={selectedLocationGroupsData}
          selectedScheduleItems={selectedScheduleItems}
          onSelectScheduleItem={handleSelectScheduleItem}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      ),
      footer: (
        <Footer
          cancelLabel={lang.back}
          saveLabel={lang.save}
          onCancel={handleBack}
          onSave={() => saveModal.onToggleModal()}
          disableSave={requestScheduleItems.length === 0}
          showFooterActions
        />
      ),
    },
  ];

  return (
    <ComponentBulkReplaceContainer>
      <Header
        showExportButton={activeStep === 1}
        onExportClick={handleExportClick}
        hasData={scheduleList?.data && scheduleList.data.length > 0}
      />
      <GenericStepper steps={steps} activeStep={activeStep} hideStepperHeader />
      <CSVLink
        data={csvData}
        headers={BULK_REPLACE_EXPORT_HEADERS}
        filename={`Bulk_Replace_${dayjs().format('MM-DD-YYYY')}.csv`}
        target="_blank"
        style={{ textDecoration: 'none' }}
        ref={csvLinkRef}
        enclosingCharacter=""
        separator=","
      />
      <PublicationModal
        open={publishModal.open}
        onClose={handlePublishModalClose}
        title={lang.bulkReplaceComponentModal.title}
        subTitle={lang.bulkReplaceComponentModal.subTitle}
        publicationType={PublicationTypes.FULL}
        locationGroupIDs={Array.from(selectedLocationGroups)}
      />
      <SimpleDialog
        open={saveModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        modalSize="sm"
        title={lang.bulkReplaceComponentSaveModal.title}
        subTitle={
          <Grid>
            <Typography sx={{ marginBottom: '10px' }}>
              {lang.bulkReplaceComponentSaveModal.subTitle}
            </Typography>
            <Typography>
              {lang.bulkReplaceComponentSaveModal.secondSubTitle}
            </Typography>
            {checkComponentTypeIsDifferent && (
              <Typography sx={{ marginTop: '10px' }}>
                {lang.bulkReplaceComponentSaveModal.componentTypeMessage}
              </Typography>
            )}
          </Grid>
        }
        onClose={saveModal.onCloseModal}
        onClickCancel={saveModal.onCloseModal}
        onClickConfirm={handleSave}
        isLoading={saveBulkReplaceComponent.isLoading}
      />
    </ComponentBulkReplaceContainer>
  );
};
